/**
 * UserLevelsWorkflowScreen — User Level / Authorization Limit management
 * Layout: ERPAdvancedGrid (left) + Detail panel (right) + context menu
 */
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, getToken } from '../../lib/api';
import { Layers, Save, Trash2, X, Edit2, Plus } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import { erpToast } from '../../components/erp/ERPToast';

function authFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
}

const LEVEL_COLS = [
  { field: 'UserId',      header: 'User ID',    frozen: true  },
  { field: 'UserName',    header: 'User Name',  frozen: false },
  { field: 'Levelofuser', header: 'Level',      frozen: false },
  { field: 'RangeType',   header: 'Range Type', frozen: false },
  { field: 'FromAmount',  header: 'From',       frozen: false },
  { field: 'ToAmount',    header: 'To',         frozen: false },
  { field: 'Desc1',       header: 'Description',frozen: false },
];

const EMPTY_FORM = { UserId: '', RangeType: 'Amount', FromAmount: 0, ToAmount: 0, Levelofuser: '1', Desc1: '' };

export default function UserLevelsWorkflowScreen({ theme, onBack }) {
  const isDark = theme === 'dark';

  const [levels,      setLevels]      = useState([]);
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isNew,       setIsNew]       = useState(false);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(20);
  const [sortField,   setSortField]   = useState('UserId');
  const [sortDir,     setSortDir]     = useState('asc');
  const [filters,     setFilters]     = useState({});
  const [ctxMenu,     setCtxMenu]     = useState(null);
  const ctxRef = useRef(null);

  const panel = isDark ? '#161b27' : '#ffffff';
  const bd    = isDark ? '#1e2533' : '#e2e8f0';
  const tx    = isDark ? '#e2e8f0' : '#1e293b';
  const mt    = isDark ? '#64748b' : '#94a3b8';
  const ac    = '#6366f1';

  // ── async helpers (defined before useEffect, called from click handlers only) ─
  const refreshLevels = async () => {
    setLoading(true);
    try {
      const d = await authFetch('/api/uams/levels').then(r => r.json());
      if (d.success) setLevels(d.data || []);
    } catch (_) { erpToast.error('Failed to reload levels'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!formData.UserId && isNew) { erpToast.error('User is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const d = await authFetch('/api/uams/levels', {
          method: 'POST',
          body: JSON.stringify({
            UserId: formData.UserId,
            RangeType: formData.RangeType,
            FromAmount: Number(formData.FromAmount) || 0,
            ToAmount: Number(formData.ToAmount) || 0,
            Levelofuser: formData.Levelofuser,
            Desc1: formData.Desc1,
          }),
        }).then(r => r.json());
        if (d.success) {
          erpToast.success('Level entry created');
          setIsNew(false);
          setFormData(EMPTY_FORM);
          await refreshLevels();
        } else { erpToast.error(d.error || 'Create failed'); }
      } else {
        const d = await authFetch(`/api/uams/levels/${selectedRow.UserId}`, {
          method: 'PUT',
          body: JSON.stringify({
            RangeType: formData.RangeType,
            FromAmount: Number(formData.FromAmount) || 0,
            ToAmount: Number(formData.ToAmount) || 0,
            Levelofuser: formData.Levelofuser,
            Desc1: formData.Desc1,
          }),
        }).then(r => r.json());
        if (d.success) {
          erpToast.success('Level saved');
          await refreshLevels();
        } else { erpToast.error(d.error || 'Save failed'); }
      }
    } catch (_) { erpToast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (level) => {
    if (!window.confirm(`Delete level entry for "${level.UserName || level.UserId}"?\nThis action cannot be undone.`)) return;
    try {
      const d = await authFetch(`/api/uams/levels/${level.UserId}`, { method: 'DELETE' }).then(r => r.json());
      if (d.success) {
        erpToast.success('Level entry deleted');
        if (selectedRow?.UserId === level.UserId) { setSelectedRow(null); setFormData(EMPTY_FORM); }
        await refreshLevels();
      } else { erpToast.error(d.error || 'Delete failed'); }
    } catch (_) { erpToast.error('Network error'); }
  };

  // ── useEffect — initial data load, inline async (project ESLint pattern) ──────
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [lRes, uRes] = await Promise.all([
          authFetch('/api/uams/levels').then(r => r.json()),
          authFetch('/api/uams/users-dropdown').then(r => r.json()),
        ]);
        if (lRes.success) setLevels(lRes.data || []);
        if (uRes.success) setUsers(uRes.data || []);
      } catch (_) { erpToast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    init();
  }, []);

  // Outside-click dismisses context menu
  useEffect(() => {
    const handler = (e) => {
      if (ctxRef.current && !ctxRef.current.contains(e.target)) setCtxMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── helpers ───────────────────────────────────────────────────────────────────
  const openEdit = (row) => {
    setIsNew(false);
    setSelectedRow(row);
    setFormData({
      UserId: row.UserId,
      RangeType: row.RangeType || 'Amount',
      FromAmount: row.FromAmount || 0,
      ToAmount: row.ToAmount || 0,
      Levelofuser: row.Levelofuser || '1',
      Desc1: row.Desc1 || '',
    });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const tr = e.target.closest('tr[data-testid]');
    if (!tr) return;
    const testId = tr.getAttribute('data-testid');
    const parts = testId.split('-row-');
    if (parts.length < 2) return;
    const rowId = parts[1];
    const row = levels.find(l => l.UserId === rowId);
    if (row) setCtxMenu({ x: e.clientX, y: e.clientY, row });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };

  // ── derived data ──────────────────────────────────────────────────────────────
  const filtered = levels.filter(l =>
    Object.entries(filters).every(([k, v]) => !v || String(l[k] || '').toLowerCase().includes(v.toLowerCase()))
  );
  const sorted = [...filtered].sort((a, b) => {
    const cmp = String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);
  const panelOpen = isNew || !!selectedRow;

  return (
    <div
      data-testid="user-levels-screen"
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      onContextMenu={handleContextMenu}
    >
      {/* Context Menu */}
      {ctxMenu && (
        <div
          ref={ctxRef}
          data-testid="levels-ctx-menu"
          style={{
            position: 'fixed', top: ctxMenu.y, left: ctxMenu.x,
            zIndex: 9999, background: panel, border: `1px solid ${bd}`,
            borderRadius: 6, boxShadow: '0 4px 20px rgba(0,0,0,.18)',
            minWidth: 160, overflow: 'hidden',
          }}
        >
          <div
            data-testid="ctx-edit-level"
            onClick={() => { openEdit(ctxMenu.row); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: tx }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = isDark ? '#1e2533' : '#f1f5f9'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <Edit2 size={12} color={ac} /> Edit Level
          </div>
          <div style={{ borderTop: `1px solid ${bd}`, margin: '2px 0' }} />
          <div
            data-testid="ctx-delete-level"
            onClick={() => { handleDelete(ctxMenu.row); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: '#ef4444' }}
            onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.background = 'transparent'; }}
          >
            <Trash2 size={12} /> Delete Level
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Left: Grid */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, minWidth: 0 }}>
          <ERPAdvancedGrid
            title="User Levels"
            columns={LEVEL_COLS}
            data={paginated}
            total={filtered.length}
            idField="UserId"
            nameField="UserName"
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={s => { setPageSize(s); setPage(1); }}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            filters={filters}
            onFilterChange={f => { setFilters(f); setPage(1); }}
            loading={loading}
            onAddNew={() => { setIsNew(true); setSelectedRow(null); setFormData(EMPTY_FORM); }}
            onEdit={openEdit}
            onVoid={handleDelete}
            testId="user-levels-grid"
            selectedRowId={selectedRow?.UserId}
            onRowClick={openEdit}
            onBack={onBack}
          />
        </div>

        {/* Right: Detail Panel */}
        {panelOpen && (
          <div
            data-testid="levels-detail-panel"
            style={{ width: 360, flexShrink: 0, borderLeft: `1px solid ${bd}`, background: panel, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px', borderBottom: `1px solid ${bd}`, background: isDark ? '#1a2035' : '#f8fafc', flexShrink: 0 }}>
              <Layers size={14} color={ac} />
              <span style={{ fontWeight: 700, fontSize: 12, color: tx, marginLeft: 6, flex: 1 }}>
                {isNew ? 'New Level Entry' : `Level: ${selectedRow?.UserName || selectedRow?.UserId}`}
              </span>
              <button
                data-testid="close-level-panel"
                onClick={() => { setSelectedRow(null); setIsNew(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mt, padding: 2 }}
              >
                <X size={14} />
              </button>
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>USER *</div>
                  {isNew ? (
                    <select
                      data-testid="level-user-select"
                      value={formData.UserId}
                      onChange={e => setFormData(p => ({ ...p, UserId: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx }}
                    >
                      <option value="">-- Select User --</option>
                      {users.map(u => (
                        <option key={u.UserId} value={u.UserId}>{u.UserName} ({u.UserId})</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ padding: '5px 8px', background: isDark ? '#1e2533' : '#f8fafc', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, color: mt }}>
                      {selectedRow?.UserName} ({selectedRow?.UserId})
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>RANGE TYPE</div>
                  <div style={{ display: 'flex', gap: 16 }}>
                    {['Amount', 'Actual Position'].map(v => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }}>
                        <input
                          type="radio"
                          name="rangetype"
                          data-testid={`rangetype-${v.replace(' ', '-')}`}
                          checked={formData.RangeType === v}
                          onChange={() => setFormData(p => ({ ...p, RangeType: v }))}
                          style={{ accentColor: ac }}
                        />
                        <span style={{ color: tx }}>{v}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>FROM AMOUNT</div>
                    <input
                      data-testid="level-from-input"
                      type="number"
                      value={formData.FromAmount}
                      onChange={e => setFormData(p => ({ ...p, FromAmount: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>TO AMOUNT</div>
                    <input
                      data-testid="level-to-input"
                      type="number"
                      value={formData.ToAmount}
                      onChange={e => setFormData(p => ({ ...p, ToAmount: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>LEVEL OF USER</div>
                  <input
                    data-testid="level-value-input"
                    value={formData.Levelofuser}
                    onChange={e => setFormData(p => ({ ...p, Levelofuser: e.target.value }))}
                    placeholder="e.g. 1, 2, 3 or Manager"
                    style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: mt, marginBottom: 3 }}>DESCRIPTION</div>
                  <input
                    data-testid="level-desc-input"
                    value={formData.Desc1}
                    onChange={e => setFormData(p => ({ ...p, Desc1: e.target.value }))}
                    placeholder="Optional description"
                    style={{ width: '100%', padding: '6px 8px', border: `1px solid ${bd}`, borderRadius: 4, fontSize: 12, background: panel, color: tx, boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    data-testid="save-level-btn"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 0', background: saving ? '#a5b4fc' : ac, color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}
                  >
                    <Save size={13} /> {saving ? 'Saving...' : (isNew ? 'Create Entry' : 'Save Changes')}
                  </button>
                  <button
                    data-testid="cancel-level-btn"
                    onClick={() => { setSelectedRow(null); setIsNew(false); }}
                    style={{ padding: '7px 12px', background: 'transparent', border: `1px solid ${bd}`, borderRadius: 5, color: mt, fontSize: 12, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
                {!isNew && selectedRow && (
                  <button
                    data-testid="delete-level-btn"
                    onClick={() => handleDelete(selectedRow)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '6px 0', background: 'transparent', border: `1px solid #fca5a5`, borderRadius: 5, color: '#ef4444', fontSize: 11, cursor: 'pointer', marginTop: 4 }}
                  >
                    <Trash2 size={12} /> Delete Entry
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!panelOpen && !loading && (
          <div style={{ width: 200, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: mt, fontSize: 11, fontStyle: 'italic', borderLeft: `1px solid ${bd}`, background: isDark ? '#0f1117' : '#f8fafc' }}>
            <div style={{ textAlign: 'center', padding: 12 }}>
              <Layers size={20} color={bd} />
              <div style={{ marginTop: 6 }}>Click a row to edit or use Add New</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
